import { IPreguntaCargaDTO, IPreguntaDTO, PreguntaCargaDTO, PreguntaDTO } from "./Pregunta.DTO";
import { Nilable } from "@shared/utils";

interface IEncuestaVaciaDTO {
    name: string;
}

interface IEncuestaBaseDTO extends IEncuestaVaciaDTO {
    id: number;
    hash: string;
    processed: boolean;
    score_earning: number;
    updated_at: string;
    created_at: string;
    notification_text: Nilable<string>;
    friendly_name: Nilable<string>;
    internal_description: Nilable<string>;
    questions_count: number;
    unsolved_bugs: number;
    approved: boolean;
    approved_by: Nilable<string>;
    research_id: Nilable<number>;
    exclude_from_ml: boolean;
}

export interface IEncuestaDTO extends IEncuestaBaseDTO {
    first_question: number;
    questions: Nilable<IPreguntaDTO[]>;
    public_url?: Nilable<string>;
}

export interface IEncuestaCargaDTO extends IEncuestaBaseDTO {
    first_question: string;
    questions: IPreguntaCargaDTO[];
}

export class EncuestaVaciaDTO {
    public nombre = "";

    constructor(json: Nilable<Partial<IEncuestaVaciaDTO>> = null) {
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IEncuestaVaciaDTO>) {
        if (json.name != null) {
            this.nombre = json.name;
        }
    }

    public toJSON(): IEncuestaVaciaDTO {
        return {
            name: this.nombre
        };
    }
}

class EncuestaBaseDTO extends EncuestaVaciaDTO {
    public id = 0;
    public hash = "";
    public procesado = false;
    public puntajeAGanar = 0;
    public actualizadoEl = "";
    public creadoEl = new Date();
    public textoNotificacion: Nilable<string>;
    public nombreAmigable: Nilable<string>;
    public descripcionInterna: Nilable<string>;
    public cantidadPreguntas = 0;
    public cantidadBugsSinResolver = 0;
    public aprobadoPor: Nilable<string>;
    public investigacionId: Nilable<number>;
    public aprobado = false;
    public excludeFromMl = false;

    constructor(json: Nilable<Partial<IEncuestaBaseDTO>> = null) {
        super();
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IEncuestaBaseDTO>) {
        super.cargarDTO(json);
        if (json.id != null) {
            this.id = json.id;
        }
        if (json.hash != null) {
            this.hash = json.hash;
        }
        if (json.processed) {
            this.procesado = json.processed;
        }
        if (json.score_earning != null) {
            this.puntajeAGanar = json.score_earning;
        }
        if (json.updated_at != null) {
            this.actualizadoEl = json.updated_at;
        }
        if (json.created_at != null) {
            this.creadoEl = new Date(json.created_at);
        }
        if (json.notification_text != null) {
            this.textoNotificacion = json.notification_text;
        }
        if (json.friendly_name != null) {
            this.nombreAmigable = json.friendly_name;
        }
        if (json.internal_description != null) {
            this.descripcionInterna = json.internal_description;
        }
        if (json.questions_count != null) {
            this.cantidadPreguntas = json.questions_count;
        }
        if (json.unsolved_bugs != null) {
            this.cantidadBugsSinResolver = json.unsolved_bugs;
        }
        if (json.approved_by != null) {
            this.aprobadoPor = json.approved_by;
        }
        if (json.research_id != null) {
            this.investigacionId = json.research_id;
        }
        if (json.approved != null) {
            this.aprobado = json.approved;
        }
        if (json.exclude_from_ml != null) {
            this.excludeFromMl = json.exclude_from_ml;
        }
    }

    public toJSON(): IEncuestaBaseDTO {
        return {
            ...super.toJSON(),
            id: this.id,
            hash: this.hash,
            processed: this.procesado,
            score_earning: this.puntajeAGanar,
            updated_at: this.actualizadoEl,
            created_at: this.creadoEl.toJSON(),
            friendly_name: this.nombreAmigable,
            internal_description: this.descripcionInterna,
            questions_count: this.cantidadPreguntas,
            unsolved_bugs: this.cantidadBugsSinResolver,
            research_id: this.investigacionId,
            approved: this.aprobado,
            notification_text: this.textoNotificacion,
            approved_by: this.aprobadoPor,
            exclude_from_ml: this.excludeFromMl
        };
    }
}

export class EncuestaDTO extends EncuestaBaseDTO {
    public primeraPregunta = 1;
    public preguntas: Nilable<PreguntaDTO[]>;
    public urlPublica = "";

    constructor(json: Nilable<Partial<IEncuestaDTO>> = null) {
        super();
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IEncuestaDTO>) {
        super.cargarDTO(json);
        if (json.first_question != null) {
            this.primeraPregunta = json.first_question;
        }
        if (json.questions != null) {
            this.preguntas = json.questions.map(pregunta => new PreguntaDTO(pregunta));
        }
        if (json.public_url != null) {
            this.urlPublica = json.public_url;
        }
    }

    public toJSON(): IEncuestaDTO {
        return {
            ...super.toJSON(),
            first_question: this.primeraPregunta,
            questions: this.preguntas?.map(pregunta => pregunta.toJSON()) ?? null
        };
    }
}

export class EncuestaCargaDTO extends EncuestaBaseDTO {
    public primeraPregunta = "";
    public preguntas: PreguntaCargaDTO[] = [];

    constructor(json: Nilable<Partial<IEncuestaCargaDTO>> = null) {
        super();
        if (json != null) {
            this.cargarDTO(json);
        }
    }

    public cargarDTO(json: Partial<IEncuestaCargaDTO>) {
        super.cargarDTO(json);
        if (json.first_question != null) {
            this.primeraPregunta = json.first_question;
        }
        if (json.questions != null) {
            this.preguntas = json.questions.map(pregunta => new PreguntaCargaDTO(pregunta));
        }
    }

    public toJSON(): IEncuestaCargaDTO {
        return {
            ...super.toJSON(),
            first_question: this.primeraPregunta,
            questions: this.preguntas?.map(pregunta => pregunta.toJSON())
        };
    }
}
